/*!
 * devextreme-angular-test
 * Version: 17.2.8
 * Build date: Mon Feb 05 2018
 *
 * Copyright (c) 2012 - 2018 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */











import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    Input,
    Output,
    OnDestroy,
    Injector,
    EventEmitter,
    OnChanges,
    DoCheck,
    SimpleChanges,
    ContentChildren,
    QueryList
} from '@angular/core';


import DevExpress from 'devextreme/bundles/dx.all';

import DxTileView from 'devextreme/ui/tile_view';


import { DxComponent } from '../core/component';
import { DxTemplateHost } from '../core/template-host';
import { DxTemplateModule } from '../core/template';
import { EventsRegistrator } from '../core/events-strategy';
import { NestedOptionHost } from '../core/nested-option';
import { WatcherHelper } from '../core/watcher-helper';
import { IterableDifferHelper } from '../core/iterable-differ-helper';

import { DxiItemModule } from './nested/item-dxi';

import { DxiItemComponent } from './nested/item-dxi';




/**
 * The TileView widget contains a collection of tiles. Tiles can store much more information than ordinary buttons, that is why they are very popular in apps designed for touch devices.
 */

export class DxTileViewComponent extends DxComponent implements OnDestroy, OnChanges, DoCheck {
    instance: DxTileView;


    /**
     * Specifies the shortcut key that sets focus on the widget.
     */
    
    get accessKey(): string {
        return this._getOption('accessKey');
    }
    set accessKey(value: string) {
        this._setOption('accessKey', value);
    }


    /**
     * A Boolean value specifying whether or not the widget changes its state when interacting with a user.
     */
    
    get activeStateEnabled(): boolean {
        return this._getOption('activeStateEnabled');
    }
    set activeStateEnabled(value: boolean) {
        this._setOption('activeStateEnabled', value);
    }


    /**
     * Specifies the height of the base tile view item.
     */
    
    get baseItemHeight(): number {
        return this._getOption('baseItemHeight');
    }
    set baseItemHeight(value: number) {
        this._setOption('baseItemHeight', value);
    }


    /**
     * Specifies the width of the base tile view item.
     */
    
    get baseItemWidth(): number {
        return this._getOption('baseItemWidth');
    }
    set baseItemWidth(value: number) {
        this._setOption('baseItemWidth', value);
    }


    /**
     * A data source used to fetch data to be displayed by the widget.
     */
    
    get dataSource(): DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<string|
        DevExpress.ui.CollectionWidgetItemTemplate> {
        return this._getOption('dataSource');
    }
    set dataSource(value: DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<string|
        DevExpress.ui.CollectionWidgetItemTemplate>) {
        this._setOption('dataSource', value);
    }


    /**
     * Specifies whether tiles are placed horizontally or vertically.
     */
    
    get direction(): string {
        return this._getOption('direction');
    }
    set direction(value: string) {
        this._setOption('direction', value);
    }


    /**
     * Specifies whether the widget responds to user interaction.
     */
    
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Specifies the attributes to be attached to the widget's root element.
     */
    
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Specifies whether the widget can be focused using keyboard navigation.
     */
    
    get focusStateEnabled(): boolean {
        return this._getOption('focusStateEnabled');
    }
    set focusStateEnabled(value: boolean) {
        this._setOption('focusStateEnabled', value);
    }


    /**
     * Specifies the widget's height.
     */
    
    get height(): number|
        string {
        return this._getOption('height');
    }
    set height(value: number|
        string) {
        this._setOption('height', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the widget.
     */
    
    get hint(): string {
        return this._getOption('hint');
    }
    set hint(value: string) {
        this._setOption('hint', value);
    }


    /**
     * Specifies whether the widget changes its state when a user pauses on it.
     */
    
    get hoverStateEnabled(): boolean {
        return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value: boolean) {
        this._setOption('hoverStateEnabled', value);
    }


    /**
     * The time period in milliseconds before the onItemHold event is raised.
     */
    
    get itemHoldTimeout(): number {
        return this._getOption('itemHoldTimeout');
    }
    set itemHoldTimeout(value: number) {
        this._setOption('itemHoldTimeout', value);
    }


    /**
     * Specifies the distance in pixels between adjacent tiles.
     */
    
    get itemMargin(): number {
        return this._getOption('itemMargin');
    }
    set itemMargin(value: number) {
        this._setOption('itemMargin', value);
    }


    /**
     * An array of items displayed by the widget.
     */
    
    get items(): Array<string|
        any|
        { disabled?: boolean, heightRatio?: number, html?: string, template?: any, text?: string, visible?: boolean, widthRatio?: number }> {
        return this._getOption('items');
    }
    set items(value: Array<string|
        any|
        { disabled?: boolean, heightRatio?: number, html?: string, template?: any, text?: string, visible?: boolean, widthRatio?: number }>) {
        this._setOption('items', value);
    }


    /**
     * Specifies a custom template for an item.
     */
    
    get itemTemplate(): any {
        return this._getOption('itemTemplate');
    }
    set itemTemplate(value: any) {
        this._setOption('itemTemplate', value);
    }


    /**
     * The text or HTML markup displayed by the widget if the item collection is empty.
     */
    
    get noDataText(): string {
        return this._getOption('noDataText');
    }
    set noDataText(value: string) {
        this._setOption('noDataText', value);
    }


    /**
     * Switches the widget to a right-to-left representation.
     */
    
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * A Boolean value specifying whether or not to display a scrollbar.
     */
    
    get showScrollbar(): boolean {
        return this._getOption('showScrollbar');
    }
    set showScrollbar(value: boolean) {
        this._setOption('showScrollbar', value);
    }


    /**
     * Specifies the number of the element when the Tab key is used for navigating.
     */
    
    get tabIndex(): number {
        return this._getOption('tabIndex');
    }
    set tabIndex(value: number) {
        this._setOption('tabIndex', value);
    }


    /**
     * Specifies whether the widget is visible.
     */
    
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the widget's width.
     */
    
    get width(): number|
        Function|
        string {
        return this._getOption('width');
    }
    set width(value: number|
        Function|
        string) {
        this._setOption('width', value);
    }

    /**
     * A handler for the contentReady event. Executed when the widget's content is ready. This handler may be executed multiple times during the widget's lifetime depending on the number of times its content changes.
     */
     onContentReady: EventEmitter<any>;

    /**
     * A handler for the disposing event. Executed when the widget is removed from the DOM using the remove(), empty(), or html() jQuery methods only.
     */
     onDisposing: EventEmitter<any>;

    /**
     * A handler for the initialized event. Executed only once, after the widget is initialized.
     */
     onInitialized: EventEmitter<any>;

    /**
     * A handler for the itemClick event.
     */
     onItemClick: EventEmitter<any>;

    /**
     * A handler for the itemContextMenu event.
     */
     onItemContextMenu: EventEmitter<any>;

    /**
     * A handler for the itemHold event.
     */
     onItemHold: EventEmitter<any>;

    /**
     * A handler for the itemRendered event.
     */
     onItemRendered: EventEmitter<any>;

    /**
     * A handler for the optionChanged event. Executed after an option of the widget is changed.
     */
     onOptionChanged: EventEmitter<any>;

    /**
     * A handler for the accessKeyChange event.
     */
     accessKeyChange: EventEmitter<string>;

    /**
     * A handler for the activeStateEnabledChange event.
     */
     activeStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the baseItemHeightChange event.
     */
     baseItemHeightChange: EventEmitter<number>;

    /**
     * A handler for the baseItemWidthChange event.
     */
     baseItemWidthChange: EventEmitter<number>;

    /**
     * A handler for the dataSourceChange event.
     */
     dataSourceChange: EventEmitter<DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<string|
        DevExpress.ui.CollectionWidgetItemTemplate>>;

    /**
     * A handler for the directionChange event.
     */
     directionChange: EventEmitter<string>;

    /**
     * A handler for the disabledChange event.
     */
     disabledChange: EventEmitter<boolean>;

    /**
     * A handler for the elementAttrChange event.
     */
     elementAttrChange: EventEmitter<any>;

    /**
     * A handler for the focusStateEnabledChange event.
     */
     focusStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the heightChange event.
     */
     heightChange: EventEmitter<number|
        string>;

    /**
     * A handler for the hintChange event.
     */
     hintChange: EventEmitter<string>;

    /**
     * A handler for the hoverStateEnabledChange event.
     */
     hoverStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the itemHoldTimeoutChange event.
     */
     itemHoldTimeoutChange: EventEmitter<number>;

    /**
     * A handler for the itemMarginChange event.
     */
     itemMarginChange: EventEmitter<number>;

    /**
     * A handler for the itemsChange event.
     */
     itemsChange: EventEmitter<Array<string|
        any|
        { disabled?: boolean, heightRatio?: number, html?: string, template?: any, text?: string, visible?: boolean, widthRatio?: number }>>;

    /**
     * A handler for the itemTemplateChange event.
     */
     itemTemplateChange: EventEmitter<any>;

    /**
     * A handler for the noDataTextChange event.
     */
     noDataTextChange: EventEmitter<string>;

    /**
     * A handler for the rtlEnabledChange event.
     */
     rtlEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the showScrollbarChange event.
     */
     showScrollbarChange: EventEmitter<boolean>;

    /**
     * A handler for the tabIndexChange event.
     */
     tabIndexChange: EventEmitter<number>;

    /**
     * A handler for the visibleChange event.
     */
     visibleChange: EventEmitter<boolean>;

    /**
     * A handler for the widthChange event.
     */
     widthChange: EventEmitter<number|
        Function|
        string>;




    
    get itemsChildren(): QueryList<DxiItemComponent> {
        return this._getOption('items');
    }
    set itemsChildren(value) {
        this.setChildren('items', value);
    }


    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost, injector: Injector,
            private _watcherHelper: WatcherHelper,
            private _idh: IterableDifferHelper, optionHost: NestedOptionHost) {

        super(elementRef, ngZone, templateHost, _watcherHelper);
        injector.get(EventsRegistrator);

        this._createEventEmitters([
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'itemClick', emit: 'onItemClick' },
            { subscribe: 'itemContextMenu', emit: 'onItemContextMenu' },
            { subscribe: 'itemHold', emit: 'onItemHold' },
            { subscribe: 'itemRendered', emit: 'onItemRendered' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { emit: 'accessKeyChange' },
            { emit: 'activeStateEnabledChange' },
            { emit: 'baseItemHeightChange' },
            { emit: 'baseItemWidthChange' },
            { emit: 'dataSourceChange' },
            { emit: 'directionChange' },
            { emit: 'disabledChange' },
            { emit: 'elementAttrChange' },
            { emit: 'focusStateEnabledChange' },
            { emit: 'heightChange' },
            { emit: 'hintChange' },
            { emit: 'hoverStateEnabledChange' },
            { emit: 'itemHoldTimeoutChange' },
            { emit: 'itemMarginChange' },
            { emit: 'itemsChange' },
            { emit: 'itemTemplateChange' },
            { emit: 'noDataTextChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'showScrollbarChange' },
            { emit: 'tabIndexChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' }
        ]);

        this._idh.setHost(this);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {
        return new DxTileView(element, options);
    }

    ngOnDestroy() {
        this._destroyWidget();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setupChanges('dataSource', changes);
        this.setupChanges('items', changes);
    }

    setupChanges(prop: string, changes: SimpleChanges) {
        if (!(prop in this._optionsToUpdate)) {
            this._idh.setup(prop, changes);
        }
    }

    ngDoCheck() {
        this._idh.doCheck('dataSource');
        this._idh.doCheck('items');
        this._watcherHelper.checkWatchers();
        super.ngDoCheck();
    }

    _setOption(name: string, value: any) {
        let isSetup = this._idh.setupSingle(name, value);
        let isChanged = this._idh.getChanges(name, value) !== null;

        if (isSetup || isChanged) {
            super._setOption(name, value);
        }
    }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'dx-tile-view',
    template: '',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        NestedOptionHost,
        IterableDifferHelper
    ]
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: NgZone, },
{type: DxTemplateHost, },
{type: Injector, },
{type: WatcherHelper, },
{type: IterableDifferHelper, },
{type: NestedOptionHost, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'accessKey': [{ type: Input },],
'activeStateEnabled': [{ type: Input },],
'baseItemHeight': [{ type: Input },],
'baseItemWidth': [{ type: Input },],
'dataSource': [{ type: Input },],
'direction': [{ type: Input },],
'disabled': [{ type: Input },],
'elementAttr': [{ type: Input },],
'focusStateEnabled': [{ type: Input },],
'height': [{ type: Input },],
'hint': [{ type: Input },],
'hoverStateEnabled': [{ type: Input },],
'itemHoldTimeout': [{ type: Input },],
'itemMargin': [{ type: Input },],
'items': [{ type: Input },],
'itemTemplate': [{ type: Input },],
'noDataText': [{ type: Input },],
'rtlEnabled': [{ type: Input },],
'showScrollbar': [{ type: Input },],
'tabIndex': [{ type: Input },],
'visible': [{ type: Input },],
'width': [{ type: Input },],
'onContentReady': [{ type: Output },],
'onDisposing': [{ type: Output },],
'onInitialized': [{ type: Output },],
'onItemClick': [{ type: Output },],
'onItemContextMenu': [{ type: Output },],
'onItemHold': [{ type: Output },],
'onItemRendered': [{ type: Output },],
'onOptionChanged': [{ type: Output },],
'accessKeyChange': [{ type: Output },],
'activeStateEnabledChange': [{ type: Output },],
'baseItemHeightChange': [{ type: Output },],
'baseItemWidthChange': [{ type: Output },],
'dataSourceChange': [{ type: Output },],
'directionChange': [{ type: Output },],
'disabledChange': [{ type: Output },],
'elementAttrChange': [{ type: Output },],
'focusStateEnabledChange': [{ type: Output },],
'heightChange': [{ type: Output },],
'hintChange': [{ type: Output },],
'hoverStateEnabledChange': [{ type: Output },],
'itemHoldTimeoutChange': [{ type: Output },],
'itemMarginChange': [{ type: Output },],
'itemsChange': [{ type: Output },],
'itemTemplateChange': [{ type: Output },],
'noDataTextChange': [{ type: Output },],
'rtlEnabledChange': [{ type: Output },],
'showScrollbarChange': [{ type: Output },],
'tabIndexChange': [{ type: Output },],
'visibleChange': [{ type: Output },],
'widthChange': [{ type: Output },],
'itemsChildren': [{ type: ContentChildren, args: [DxiItemComponent, ] },],
};
}


export class DxTileViewModule { static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  imports: [
    DxiItemModule,
    DxTemplateModule
  ],
  declarations: [
    DxTileViewComponent
  ],
  exports: [
    DxTileViewComponent,
    DxiItemModule,
    DxTemplateModule
  ],
  providers: [EventsRegistrator]

}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
